import React, { useContext, useMemo, useState } from 'react';
import { Avatar, Box, Text, Input, Textarea, VStack, Flex, Button, Icon, IconButton, useToast } from '@chakra-ui/react';
import { FiLock, FiMinus, FiPlus } from 'react-icons/fi';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { collabApi } from '../../../../services/api/collab';
import { Campaign, Collab } from '@foodfluence/shared';
import { sendCounterEmail, updateInfluencerHandles } from '../../../../services/flaskService';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { getInfluencerPrimaryHandle, getMissingDeliverableHandles } from '../../../../utils/influencerUtils';
import { MAX_COUNTERS, canCounter } from '../../utils/counterUtils';
import { isCreditValidCampaignType } from 'utils/collabUtils';
import MissingHandleInputs, { MissingHandleKey } from 'components/MissingHandleInputs';
import {
  UGC_RATE_PRESETS,
  applyPresetCountsToRequirements,
  getContentRequirementsAsPresets,
  getContentTypeDisplayName,
  getUgcPresetCounts,
  ugcDeliverableMatch,
} from '../../../../utils/contentUtils';
import UgcDeliverablesPicker from '../../../explore/components/UgcDeliverablesPicker';
import PostByConfirm, { usePostByConfirm } from '../PostByConfirm';
import { counterRound } from '../../utils/reviewInvite';
import { CAMPAIGN_TYPE_CHIP } from '../../../explore/utils';
import { ff } from '../../../../theme';

interface CounterCollabFormProps {
  collab: Collab;
  campaign: Campaign;
  /** Why the creator is countering, from the reason step (sent with a first counter). */
  reason?: string;
  /** Called after a successful submit (the success toast has already shown). */
  onSubmitted: () => void;
}

/**
 * The creator's counter-offer form and submit. Shared by the Counter Offer page and the in-chat
 * counter modal so counter limits, validation and the submit path stay identical.
 */
const CounterCollabForm: React.FC<CounterCollabFormProps> = ({ collab, campaign, reason, onSubmitted }) => {
  const toast = useToast();
  const clients = useContext(ClientDataContext);
  
  const client = clients[campaign.clientID];

  // Check if this is a content-based (UGC) collab
  const isContentBasedCollab =
    Object.keys(collab?.content ?? {}).length > 0 ||
    Object.keys(collab?.contentRequirements ?? {}).length > 0 ||
    Object.keys(collab?.proposedContent ?? {}).length > 0 ||
    collab?.creatorType === 'ugc';

  // The brand's current offer (their contentRequirements, else the campaign template)
  const brandOfferRequirements =
    collab?.contentRequirements && Object.keys(collab.contentRequirements).length > 0
      ? collab.contentRequirements
      : campaign?.contentRequirements ?? {};
  // Countering a brand offer seeds from their terms; editing our own pending counter
  // seeds from what we last proposed (mirrors the cash/credit seeding above)
  const baseContentRequirements =
    collab.status === 'pendingInfluencerApproval'
      ? brandOfferRequirements
      : collab?.proposedContent && Object.keys(collab.proposedContent).length > 0
        ? collab.proposedContent
        : brandOfferRequirements;

  const initialCash = collab.status === 'pendingInfluencerApproval' 
    ? collab.cash || 0 
    : collab.proposedInfluencerCash || 0;
  const cashCap = 10000;

  const initialCredit = collab.status === 'pendingInfluencerApproval'
    ? collab.credit || 0
    : collab.proposedInfluencerCredit || 0;

  const initialTikTokCount = collab.status === 'pendingInfluencerApproval'
    ? collab.deliverableCounts?.tiktoks || 0
    : collab.proposedInfluencerDeliverableCounts?.tiktoks || 0;

  const initialIgReelCount = collab.status === 'pendingInfluencerApproval'
    ? collab.deliverableCounts?.instagramReels || 0
    : collab.proposedInfluencerDeliverableCounts?.instagramReels || 0;

  const initialIgPostCount = collab.status === 'pendingInfluencerApproval'
    ? collab.deliverableCounts?.instagramPosts || 0
    : collab.proposedInfluencerDeliverableCounts?.instagramPosts || 0;

  const initialIgStoryCount = collab.status === 'pendingInfluencerApproval'
    ? collab.deliverableCounts?.instagramStories || 0
    : collab.proposedInfluencerDeliverableCounts?.instagramStories || 0;

  const [tikTokCount, setTikTokCount] = useState(Number(initialTikTokCount));
  const [igReelCount, setIgReelCount] = useState(Number(initialIgReelCount));
  const [igPostCount, setIgPostCount] = useState(Number(initialIgPostCount));
  const [igStoryCount, setIgStoryCount] = useState(Number(initialIgStoryCount));
  const [cashAmount, setCashAmount] = useState(initialCash);
  const [creditAmount, setCreditAmount] = useState(initialCredit);
  const { influencerData } = useContext(InfluencerDataContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const NOTE_MAX = 240;
  const [note, setNote] = useState(collab.influencerCounterNote || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [handleInputs, setHandleInputs] = useState<Record<MissingHandleKey, string>>({ instagramHandle: '', tiktokHandle: '' });
  // Go-live campaigns: the brand can accept a counter directly, which locks the dates — confirm them.
  const postBy = usePostByConfirm(campaign, collab);

  // For content-based collabs, only compensation changes — deliverables stay as-is
  const proposedDeliverableCounts = isContentBasedCollab ? collab.deliverableCounts : {
    tiktoks: tikTokCount,
    instagramReels: igReelCount,
    instagramPosts: igPostCount,
    instagramStories: igStoryCount,
  };
  const missingHandles = getMissingDeliverableHandles(influencerData, proposedDeliverableCounts);
  const handlesComplete = missingHandles.every((key) => handleInputs[key].trim() !== '');

  // UGC content countering: adjust preset counts; requirements that match no preset
  // (brand-authored durations etc.) are carried through unchanged
  const initialUgcPresetCounts = getUgcPresetCounts(baseContentRequirements);
  const [ugcPresetCounts, setUgcPresetCounts] = useState<Record<string, number>>(initialUgcPresetCounts);
  const unmatchedRequirements = useMemo(() => {
    const matchedIds = new Set(
      getContentRequirementsAsPresets(baseContentRequirements).map(({ requirement }) => requirement.id)
    );
    return Object.values(baseContentRequirements).filter(requirement => !matchedIds.has(requirement.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const rateByPreset = useMemo(() => {
    return (influencerData?.ugcRateRanges ?? []).reduce<Record<string, number>>((rates, rate) => {
      const preset = ugcDeliverableMatch(rate);
      if (preset && rate.minRate > 0) {
        rates[preset.displayName] = rate.minRate;
      }
      return rates;
    }, {});
  }, [influencerData?.ugcRateRanges]);
  const hasUgcContentChanges = UGC_RATE_PRESETS.some(
    preset => (ugcPresetCounts[preset.displayName] || 0) !== (initialUgcPresetCounts[preset.displayName] || 0)
  );
  const totalUgcUnits =
    Object.values(ugcPresetCounts).reduce((total, count) => total + count, 0) +
    unmatchedRequirements.length;

  const handleIncrement = (setter: React.Dispatch<React.SetStateAction<number>>, value: number) => {
    setter(value + 1);
  };

  const handleDecrement = (setter: React.Dispatch<React.SetStateAction<number>>, value: number) => {
    if (value > 0) {
      setter(value - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (missingHandles.length > 0) {
      try {
        await updateInfluencerHandles(influencerData.id, handleInputs);
      } catch (error) {
        console.error('Error verifying social handles:', error);
        toast({
          title: "We couldn't verify that handle",
          description: "Please double-check your social media handle and try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setIsSubmitting(false);
        return;
      }
    }
    try {
      // UGC collabs counter content terms instead of social deliverable counts
      const deliverableCounts = isContentBasedCollab ? undefined : proposedDeliverableCounts;
      const proposedContent = isContentBasedCollab
        ? applyPresetCountsToRequirements(baseContentRequirements, ugcPresetCounts)
        : undefined;

      // New counter on a pending invite, or an edit of the latest one; the server picks which.
      const { created } = await collabApi.counterCollab({
        collabId: collab.id,
        deliverableCounts,
        proposedContent,
        cash: Number(cashAmount),
        credit: Number(creditAmount),
        reason,
        note,
        goLiveConfirmed: postBy.goLiveConfirmed,
      }, influencerData.id);
      if (created) {
        sendCounterEmail(client.email, getInfluencerPrimaryHandle(influencerData), campaign.clientID, client.accountName);
      }
      toast({
        title: "Counter offer submitted",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSubmitted();
    } catch (error) {
      console.error('Error submitting counter offer:', error);
      setIsSubmitting(false);
      if (postBy.handleError(error)) return;
      // Server refusals (counter limit, counters not allowed, collab no longer counterable) carry a readable message.
      toast({
        title: "Error submitting counter offer",
        description: error instanceof Error ? error.message : undefined,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  const hasChanges = (): boolean => {
    if (isContentBasedCollab) {
      return cashAmount !== initialCash || creditAmount !== initialCredit || hasUgcContentChanges;
    }
    
    // For regular collabs, check both deliverables and compensation
    return (
      tikTokCount !== initialTikTokCount ||
      igReelCount !== initialIgReelCount ||
      igPostCount !== initialIgPostCount ||
      igStoryCount !== initialIgStoryCount ||
      cashAmount !== initialCash ||
      creditAmount !== initialCredit
    );
  };

  const isSubmitEnabled =
    canCounter(campaign, collab) &&
    !postBy.blockedReason &&
    (isContentBasedCollab ? totalUgcUnits > 0 : (tikTokCount > 0 || igReelCount > 0 || igPostCount > 0 || igStoryCount > 0)) &&
    (isCreditValidCampaignType(campaign?.campaignType) || cashAmount > 0 || creditAmount > 0) &&
    hasChanges() &&
    handlesComplete &&
    cashAmount >= 0 &&
    creditAmount >= 0 &&
    cashAmount <= cashCap;

  const setCash = (value: number) => {
    setCashAmount(value);
    setErrorMessage(value > cashCap ? `Cash amount cannot exceed ${cashCap.toLocaleString()}.` : null);
  };

  const handleCashChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (!isNaN(value)) setCash(value);
  };

  const handleCreditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (!isNaN(value)) {
      setCreditAmount(value);
    }
  };

  const brandName = client?.accountName || 'the brand';
  const showCredit = isCreditValidCampaignType(campaign?.campaignType);
  const brandCash = collab.cash || 0;
  const brandCredit = collab.credit || 0;
  const counterAllowed = canCounter(campaign, collab);
  const round = counterRound(collab);
  const kind = campaign?.campaignType ? CAMPAIGN_TYPE_CHIP[campaign.campaignType] : null;
  const summary = `$${cashAmount} cash${showCredit ? ` + $${creditAmount} credit` : ''}`;
  const roundLabel = !counterAllowed
    ? campaign?.allowCounters ? `Counter ${MAX_COUNTERS} of ${MAX_COUNTERS} used` : 'Counters off'
    : `${round.isEdit ? 'Editing counter' : 'Counter'} ${round.number} of ${MAX_COUNTERS}${!round.isEdit && round.remainingAfter === 0 ? ' · your last one' : ''}`;
  const sub = !counterAllowed
    ? 'Counters are locked for this collab.'
    : round.isEdit
      ? `Editing doesn't use a counter. We'll text you if ${brandName} counters.`
      : `${round.remainingAfter > 0 ? `You can send ${round.remainingAfter} more ${round.remainingAfter === 1 ? 'counter' : 'counters'} after this one.` : 'This is your last counter on this collab.'} We'll text you if ${brandName} counters.`;

  const moneyRows = [
    { key: 'cash', label: 'cash', value: cashAmount, orig: brandCash, step: 5, onInput: handleCashChange, set: setCash },
    ...(showCredit
      ? [{ key: 'credit', label: 'credit', value: creditAmount, orig: brandCredit, step: 10, onInput: handleCreditChange, set: setCreditAmount }]
      : []),
  ];

  return (
    <VStack spacing="16px" align="stretch" fontFamily="display" color={ff.ink}>
      {/* Their offer */}
      <Flex p="12px" borderRadius="16px" bg={ff.grey} align="center" gap="12px">
        <Avatar
          src={client?.instagramProfilePicture}
          name={client?.accountName}
          boxSize="44px"
          borderRadius="12px"
          bg={ff.card}
          color={ff.ink}
          flex="none"
          sx={{ '& > [role=img]': { fontSize: '14px', fontWeight: 600 }, img: { borderRadius: '12px' } }}
        />
        <VStack flex={1} minW={0} align="stretch" spacing="2px">
          <Text fontSize="16px" fontWeight={600} lineHeight="normal" noOfLines={1}>{brandName}</Text>
          <Text fontSize="13px" color="#55555A" lineHeight="normal" noOfLines={1}>
            {[campaign?.name, kind].filter(Boolean).join(' · ')}
          </Text>
        </VStack>
        <VStack flex="none" align="flex-end" spacing={0}>
          <Text textStyle="display" fontSize="22px" lineHeight={1.12} letterSpacing="0">
            ${brandCash}{showCredit ? ` + $${brandCredit}` : ''}
          </Text>
          <Text fontSize="10px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A" lineHeight="normal">
            Their offer
          </Text>
        </VStack>
      </Flex>

      <VStack align="stretch" spacing="10px" pt="4px">
        <Flex
          alignSelf="flex-start"
          h="28px"
          px="10px"
          borderRadius="full"
          align="center"
          fontSize="12px"
          fontWeight={600}
          bg={counterAllowed ? ff.ink : '#FBE1DC'}
          color={counterAllowed ? ff.lime : '#A1260F'}
        >
          {roundLabel}
        </Flex>
        <Text as="h1" m={0} textStyle="display" fontSize="29px" lineHeight={1.12} letterSpacing="0">
          {round.isEdit ? 'Edit your counter' : 'Make a counter'}
        </Text>
        <Text fontSize="15px" lineHeight={1.4} color="#55555A">{sub}</Text>
      </VStack>

      {!counterAllowed && (
        <Flex role="status" px="14px" py="12px" borderRadius="14px" bg="#FBE1DC" color="#6E1A0A" gap="10px" fontSize="14px" lineHeight={1.4}>
          <Icon as={FiLock} boxSize="20px" flex="none" mt="1px" aria-hidden />
          <Text as="span">
            {campaign?.allowCounters ? (
              <>
                <strong>Counter limit reached.</strong> You have sent {MAX_COUNTERS} of {MAX_COUNTERS} counters on this collab. Accept or decline the latest offer from {brandName}, or message them.
              </>
            ) : (
              <>
                <strong>Counters are off.</strong> {`${brandName} isn't taking counters on this invite. Accept or decline it, or message them.`}
              </>
            )}
          </Text>
        </Flex>
      )}

      <VStack spacing="16px" align="stretch" opacity={counterAllowed ? 1 : 0.45} pointerEvents={counterAllowed ? undefined : 'none'}>
        {/* Compensation steppers */}
        {moneyRows.map(row => {
          const delta = row.value - row.orig;
          return (
            <Flex key={row.key} px="14px" py="12px" borderRadius="18px" border={`1px solid ${ff.card}`} align="center" gap="12px">
              <VStack flex={1} minW={0} align="stretch" spacing="4px">
                <Text as="label" htmlFor={`counter-${row.key}`} fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2} lineHeight="normal">
                  Proposed {row.label}
                </Text>
                <Flex align="baseline" gap="8px">
                  <Flex align="baseline" minW={0}>
                    <Text as="span" textStyle="display" fontSize="34px" letterSpacing="0">$</Text>
                    <Input
                      id={`counter-${row.key}`}
                      value={row.value}
                      onChange={row.onInput}
                      inputMode="numeric"
                      variant="unstyled"
                      textStyle="display"
                      fontSize="34px"
                      lineHeight={1.12}
                      letterSpacing="0"
                      h="36px"
                      w={`${Math.max(String(row.value).length, 1) + 0.5}ch`}
                      minW="2ch"
                      maxW="7ch"
                      p={0}
                      borderRadius={0}
                    />
                  </Flex>
                  {delta !== 0 && counterAllowed && (
                    <Flex as="span" h="22px" px="7px" borderRadius="full" bg={delta > 0 ? "#E4F3CC" : "#FBE1DC"} color={delta > 0 ? "#2C5208" : "#A1260F"} align="center" fontSize="12px" fontWeight={600} flex="none">
                      {delta > 0 ? '+' : '−'}${Math.abs(delta)}
                    </Flex>
                  )}
                </Flex>
                <Text fontSize="13px" color="#55555A" lineHeight="normal">
                  {brandName} offered <Text as="b" color={ff.ink}>${row.orig}</Text>
                  {row.key === 'cash' && campaign.campaignType === 'retail' && !campaign?.productValue ? ' · includes the product cost' : ''}
                </Text>
              </VStack>
              <StepButton label={`Lower ${row.label}`} onClick={() => row.set(Math.max(0, row.value - row.step))} />
              <StepButton label={`Raise ${row.label}`} plus onClick={() => row.set(row.value + row.step)} />
            </Flex>
          );
        })}

        {/* Social deliverables — counterable for non-UGC collabs */}
        {!isContentBasedCollab && (
          <VStack align="stretch" spacing="8px">
            <Text fontSize="14px" fontWeight={600} lineHeight="normal">Deliverables</Text>
            <Box>
              {[
                { label: 'TikTok', logo: FaTiktok, count: tikTokCount, setCount: setTikTokCount },
                { label: 'IG Reel', logo: FaInstagram, count: igReelCount, setCount: setIgReelCount },
                { label: 'IG Post', logo: FaInstagram, count: igPostCount, setCount: setIgPostCount },
                { label: 'IG Story', logo: FaInstagram, count: igStoryCount, setCount: setIgStoryCount },
              ].map(({ label, logo, count, setCount }) => (
                <Flex key={label} align="center" gap="12px" minH="64px" borderTop={`1px solid ${ff.line}`}>
                  <Icon as={logo} boxSize="20px" flex="none" aria-hidden />
                  <Text flex={1} fontSize="15px" fontWeight={600} lineHeight="normal">{label}</Text>
                  <StepButton label={`Decrease ${label}`} onClick={() => handleDecrement(setCount, count)} isDisabled={count === 0} />
                  <Text as="span" textStyle="display" fontSize="28px" letterSpacing="0" minW="24px" textAlign="center" aria-live="polite">
                    {count}
                  </Text>
                  <StepButton label={`Increase ${label}`} plus onClick={() => handleIncrement(setCount, count)} />
                </Flex>
              ))}
            </Box>
          </VStack>
        )}

        {/* UGC content deliverables — counterable at preset granularity */}
        {isContentBasedCollab && (
          <VStack align="stretch" spacing="8px">
            <Text fontSize="14px" fontWeight={600} lineHeight="normal">Content deliverables</Text>
            <UgcDeliverablesPicker
              counts={ugcPresetCounts}
              onChange={setUgcPresetCounts}
              rateByPreset={rateByPreset}
            />
            {unmatchedRequirements.length > 0 && (
              <Box p="12px 14px" borderRadius="14px" bg={ff.grey}>
                <Text fontSize="13px" fontWeight={600} color={ff.body} mb="4px" lineHeight="normal">
                  Included as-is
                </Text>
                {unmatchedRequirements.map(requirement => (
                  <Text key={requirement.id} fontSize="14px" color={ff.body} lineHeight={1.4}>
                    {requirement.count ?? 1}× {getContentTypeDisplayName(requirement.duration, requirement.type)}
                  </Text>
                ))}
              </Box>
            )}
          </VStack>
        )}

        {/* Note (optional) */}
        <VStack as="label" align="stretch" spacing="6px">
          <Text as="span" fontSize="14px" fontWeight={600} lineHeight="normal">
            Note to {brandName} <Text as="span" fontWeight={500} color={ff.text2}>(optional)</Text>
          </Text>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            placeholder="Why this works for you, in a sentence or two"
            h="80px"
            px="14px"
            py="12px"
            borderRadius="14px"
            border={`1px solid ${ff.border}`}
            bg="white"
            fontSize="16px"
            color={ff.ink}
            resize="none"
            _hover={{ borderColor: ff.border }}
            _focusVisible={{ borderColor: ff.ink, boxShadow: `0 0 0 1px ${ff.ink}` }}
            _placeholder={{ color: ff.muted }}
          />
          <Text as="span" fontSize="12px" color={ff.text2} textAlign="right" lineHeight="normal">
            {note.length}/{NOTE_MAX}
          </Text>
        </VStack>

        <MissingHandleInputs
          missing={missingHandles}
          values={handleInputs}
          onChange={(key, value) => setHandleInputs((prev) => ({ ...prev, [key]: value }))}
        />

        {errorMessage && (
          <Text color={ff.danger} fontSize="14px" lineHeight={1.4}>
            {errorMessage}
          </Text>
        )}

        <PostByConfirm state={postBy} />
      </VStack>

      {/* Submit, pinned to the bottom of the page / modal */}
      <Box position="sticky" bottom={0} bg="white" mx="-20px" px="20px" pt="14px" pb="14px" borderTop={`1px solid ${ff.line}`} zIndex={1}>
        <Button
          aria-label="Submit"
          onClick={handleSubmit}
          w="100%"
          h="56px"
          borderRadius="full"
          bg={ff.lime}
          color={ff.ink}
          fontSize="16px"
          fontWeight={600}
          lineHeight="normal"
          _hover={{ bg: ff.lime }}
          _active={{ bg: ff.lime }}
          _disabled={{ bg: ff.line, color: '#6E6E72', cursor: 'not-allowed', opacity: 1 }}
          isDisabled={!isSubmitEnabled || isSubmitting}
          isLoading={isSubmitting}
        >
          <Text as="span" noOfLines={1}>Submit counter · {summary}</Text>
        </Button>
      </Box>
    </VStack>
  );
};

/** 44px round stepper button (board CounterForm): outlined minus, ink plus with lime glyph. */
const StepButton: React.FC<{ label: string; plus?: boolean; onClick: () => void; isDisabled?: boolean }> = ({ label, plus, onClick, isDisabled }) => (
  <IconButton
    aria-label={label}
    icon={plus ? <FiPlus size={18} strokeWidth={2.4} /> : <FiMinus size={18} strokeWidth={2.4} />}
    onClick={onClick}
    isDisabled={isDisabled}
    flex="none"
    w="44px"
    h="44px"
    minW="44px"
    borderRadius="full"
    border={plus ? 0 : `1.5px solid ${ff.ink}`}
    bg={plus ? ff.ink : 'white'}
    color={plus ? ff.lime : ff.ink}
    _hover={{ bg: plus ? ff.ink : 'white' }}
    _active={{ bg: plus ? ff.ink : 'white' }}
  />
);

export default CounterCollabForm;
